import { Seeder } from '../seeder'
import { bsConstants } from '../../module.constants'
import { LiveSeedUsers, SeedUser, TestSeedUsers } from './interfaces'
import { defaultPreferences } from '../../modules/user'

export class UserSeeder extends Seeder<TestSeedUsers, LiveSeedUsers, SeedUser> {
  type = 'user'
  // 102, not 100: CappedPublisher [sc-29038] and DelayedAmazonReader [sc-29117] come on top of the random
  // users rather than out of them
  maxModelCount = 102

  base = {
    // password: '$2b$13$gE.T8ENiwgqRyP/qlL26MO6w4ocvp4lEK87LDksqHRufiUR5e39vK', // 123
    password: '123', // Don't use hashed password for the seed as the API will hash the plain text for comparison.
    reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
    authorRole: bsConstants.USER.ROLES.AUTHOR.GROWING,
    consentMethod: bsConstants.USER.CONSENT_METHOD.SIGN_UP_PAGE,
    image: 'FakeUser.png',
    preferences: {
      ...defaultPreferences,
      reviewSitesFlag: bsConstants.ARCS.REVIEWS.ALL_FLAGS,
      reviewLanguagesFlag: bsConstants.BOOKS.LANGUAGE_FLAGS.ALL_FLAGS
    },
    confirmEmail: true,
    // Unlimited live campaigns on the Publisher plan, as every account on it before [sc-29038] has, so the
    // publishers here (and anyone a spec upgrades to it) aren't capped. CappedPublisher is the exception.
    maxRunningArcsOverride: -1
  }

  getFakeSeed (id: number): SeedUser {
    return {
      id,
      ...this.base,
      emailAddress: this.faker.internet.email(),
      name: this.faker.name.firstName() + ' ' + this.faker.name.lastName()
    }
  }

  getLiveDataObject (): LiveSeedUsers {
    return {
      BooksproutSupport: {
        id: 1,
        ...this.base,
        emailAddress: 'support@booksprout.co',
        name: 'Booksprout Support',
        image: 'booksprout-admin.svg',
        isAdmin: true,
        password: '$2b$13$uDYxj2boi7AmImwIlc.ySOuJ0hGbBI7SAd26PRWds.W4CKzfW9nIW'
      },
      DeletedUser: {
        id: 2,
        ...this.base,
        emailAddress: 'N/A',
        name: '[Deleted User]',
        image: 'booksprout-admin.svg',
        password: '-1'
      }
    }
  }

  getTestDataObject (): TestSeedUsers {
    if (process.env.BUILD_MODE === 'master') {
      return {} as unknown as TestSeedUsers
    }

    const testData = {
      Admin: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'admin@allangaunt.dev',
        name: 'Booksprout Admin User'
      },
      Me: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'me@allangaunt.dev',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        authorRole: bsConstants.USER.ROLES.AUTHOR.PUBLISHER,
        name: 'Allan Gaunt',
        image: 'AllanGaunt.png',
        pirateId: 'sameUserPirateId'
      },
      Va: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'va@allangaunt.dev',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.PRO,
        name: 'Miss Virtual Assistant',
        image: 'VA.png'
      },
      OldBsUser: { // Used to ensure password from old system matches format for new system
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'allangaunt@gmail.com',
        password: '$2y$13$GS0JW0Rg5dkuPV2ji9ULSOnN9sP2NN4YxQBWCu2B62XPwI0bGzfMS',
        authorRole: bsConstants.USER.ROLES.AUTHOR.PUBLISHER,
        name: 'Allan Gaunt Old',
        image: 'AllanGauntOld.jpg'
      },
      StanRizzo: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'stan.rizzo@allangaunt.dev',
        authorRole: void 0,
        name: 'Stan Rizzo'
      },
      DarthVadar: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'darth.vadar@allangaunt.dev',
        authorRole: void 0,
        name: 'Darth Vadar',
        image: 'FakeUser.png'
      },
      LukeSkywalker: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'luke.skywalker@allangaunt.dev',
        authorRole: void 0,
        name: 'Luke Skywalker'
      },
      MaraJade: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'mara.jade@allangaunt.dev',
        authorRole: void 0,
        name: 'Mara Jade'
      },
      HanSolo: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'han.solo@allangaunt.dev',
        authorRole: void 0,
        name: 'Han Solo',
        pirateId: 'validPirateId',
        preferences: {
          ...defaultPreferences,
          reviewSitesFlag: bsConstants.ARCS.REVIEWS.ALL_FLAGS,
          reviewLanguagesFlag: bsConstants.BOOKS.LANGUAGE_FLAGS.ALL_FLAGS
        }
      },
      Thrawn: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'thrawn@allangaunt.dev',
        authorRole: void 0,
        name: 'Admiral Thrawn'
      },
      Publisher: { // Used to ensure password from old system matches format for new system
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'publisher@allangaunt.dev',
        authorRole: bsConstants.USER.ROLES.AUTHOR.PUBLISHER,
        name: 'I Am Publisher',
        image: 'Publisher.jpg',
        // sc-29190: an application staff have reviewed, with their note, for the Publisher Information tab
        publisherApplication: {
          website: 'https://www.iampublisher.example',
          information: 'A publishing house with 30 authors across romance, fantasy and thrillers.\nWe run about 40 review campaigns a month.',
          reviewed: true,
          notes: ['Website checked: a real press with 30 authors listed. Limit left unlimited.']
        }
      },
      FreeAuthor: { // Used to ensure password from old system matches format for new system
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'free.author@allangaunt.dev',
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        name: 'Free Author'
      },
      GrowingAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'growing.author@allangaunt.dev',
        authorRole: bsConstants.USER.ROLES.AUTHOR.GROWING,
        name: 'Growing Author'
      },
      ProAuthor: { // Used to ensure password from old system matches format for new system
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'pro.author@allangaunt.dev',
        authorRole: bsConstants.USER.ROLES.AUTHOR.PRO,
        name: 'Pro Author'
      },
      BestsellingAuthor: { // Used to ensure password from old system matches format for new system
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'bestselling.author@allangaunt.dev',
        authorRole: bsConstants.USER.ROLES.AUTHOR.BEST_SELLING,
        name: 'Bestselling Author'
      },
      NonVerifiedAuthor: { // Used to ensure password from old system matches format for new system
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'non.verified@allangaunt.dev',
        authorRole: void 0,
        name: 'Non Verified Author',
        confirmEmail: false
      },
      HasReviewDueSoon: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'has.review.due.soon@allangaunt.dev',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        authorRole: void 0,
        name: 'Review Due Soon User'
      },
      ApproveUser: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'approve.test@allangaunt.dev',
        authorRole: void 0,
        name: 'Approve this user'
      },
      RejectUser: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'reject.test@allangaunt.dev',
        authorRole: void 0,
        name: 'Reject this user'
      },
      ReviewerNoAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'reviewer.no.author@allangaunt.dev',
        name: 'Reviewer Only',
        authorRole: void 0
      },
      YourArcs: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'your.arcs@allangaunt.dev',
        name: 'Your ARCS',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        lockRoles: true
      },
      ClaimNonMemberArcDownloadsLocked : {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'downloads.locked@allangaunt.dev',
        name: 'Downloads Locked',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        statuses: [
          {
            userId: 1, // id doesn't matter here. Ignored.
            type: bsConstants.USER.USER_STATUS.PREVENT_ARC_DOWNLOADS,
            active: true,
            isRestriction: true,
            additionalInformation: 'You\'ve been a very naughty boy!'
          },
          {
            userId: 1, // id doesn't matter here. Ignored.
            type: bsConstants.USER.USER_STATUS.SHADOW_BAN,
            active: false,
            isRestriction: true,
            additionalInformation: 'Naughty boy'
          },
          {
            userId: 1, // id doesn't matter here. Ignored.
            type: bsConstants.USER.USER_STATUS.LOCKED,
            active: false,
            isRestriction: true,
            additionalInformation: 'Really naughty boy'
          }
        ]
      },
      ClaimNonMemberTooManyDue: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'too.many.due@allangaunt.dev',
        name: 'TooMany Reviews',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        lockRoles: true
      },
      ClaimNonMemberDueTooLong: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'due.too.long@allangaunt.dev',
        name: 'DueToo Long',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        lockRoles: true
      },
      ClaimNonMemberWeeklyLimitReached: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'nm.weekly.limit@allangaunt.dev',
        name: 'NonMemberClaim WeeklyLimitReached',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        lockRoles: true
      },
      ClaimNonMemberWeeklyLimitReachedValued: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'nm.weekly.limit.valued@allangaunt.dev',
        name: 'NonMemberClaim WeeklyLimitReached Valued Reviewer',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        lockRoles: true
      },
      ClaimOutstandingTeamReviewNotVIP: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'has.review.for.team@allangaunt.dev',
        name: 'HasOutstanding TeamReview',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        lockRoles: true
      },
      ClaimBlockedUser: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'blocked.user@allangaunt.dev',
        name: 'Blocked User',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        lockRoles: true
      },
      UserAlreadyClaimedBookBefore: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'claimed.book.before@allangaunt.dev',
        name: 'UserClaimed BookBefore',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        lockRoles: true
      },
      ClaimUnverifiedEmail: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'unverified@allangaunt.dev',
        name: 'Unverified Email',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        lockRoles: true,
        confirmEmail: false
      },
      ClaimNewReviewerNoTeam: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'no.team.new.reviewer@allangaunt.dev',
        name: 'New reviewer no team',
        authorRole: void 0
      },
      LimitedStores: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'limited.stores@allangaunt.dev',
        name: 'Limited stores',
        authorRole: void 0,
        preferences: {
          ...defaultPreferences,
          reviewSitesFlag: bsConstants.ARCS.REVIEWS.FLAGS.KOBO | bsConstants.ARCS.REVIEWS.FLAGS.GOOGLE,
          reviewLanguagesFlag: bsConstants.BOOKS.LANGUAGE_FLAGS.ALL_FLAGS
        }
      },
      BadBoy: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'bad.boy@allangaunt.dev',
        name: '_Bad Boy',
        authorRole: void 0
      },
      NotificationAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'notif.a@allangaunt.dev',
        name: 'Author Notification',
        authorRole: bsConstants.USER.ROLES.AUTHOR.BEST_SELLING
      },
      NotificationReviewer: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'notif.r@allangaunt.dev',
        name: 'Reviewer Notification',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED
      },
      NotificationReviewerNoOtherLanguages: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'notif.r.no-other-languages@allangaunt.dev',
        name: 'Reviewer Notification No Other Languages',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        preferences: {
          ...defaultPreferences,
          alertNewArc: true,
          alertNewArcOtherLanguages: false,
          reviewSitesFlag: bsConstants.ARCS.REVIEWS.ALL_FLAGS,
          reviewLanguagesFlag: bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.ENGLISH
        }
      },
      AppliedForTopReviewer: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'applied.for.tr@allangaunt.dev',
        name: 'Top Reviewer Applicant',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        appliedForTopReviewer: true
      },
      Pirate: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'pirate@allangaunt.dev',
        name: 'Pirate',
        pirateId: 'pirateUser',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW
      },
      ShadowBan: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'shadow.ban@allangaunt.dev',
        name: 'Shadow banned user',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        statuses: [
          {
            userId: 1, // id doesn't matter here. Ignored.
            type: bsConstants.USER.USER_STATUS.SHADOW_BAN,
            active: true,
            isRestriction: true,
            additionalInformation: 'Free ban from all of us at Booksprout. Enjoy!'
          }
        ]
      },
      NoName: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'no.name@allangaunt.dev',
        name: '',
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW
      },
      ReviewerNoName: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'reviewer.no.name@allangaunt.dev',
        name: '',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW
      },
      ReviewerNoStores: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'reviewer.no.stores@allangaunt.dev',
        name: 'Reviewer - no stores',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        preferences: {
          ...defaultPreferences,
          reviewSitesFlag: bsConstants.ARCS.REVIEWS.FLAGS.NONE,
          reviewLanguagesFlag: bsConstants.BOOKS.LANGUAGE_FLAGS.ALL_FLAGS
        }
      },
      Locked: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'locked@allangaunt.dev',
        name: 'Locked user',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        statuses: [
          {
            userId: 1, // id doesn't matter here. Ignored.
            type: bsConstants.USER.USER_STATUS.LOCKED,
            active: true,
            isRestriction: true,
            additionalInformation: 'Locked with no ability to log in'
          }
        ]
      },
      RegistrationReviewer: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'registration.reviewer@allangaunt.dev',
        name: 'Registration Reviewer',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.UNSET,
        authorRole: void 0
      },
      RegistrationAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'registration.author@allangaunt.dev',
        name: 'Registration Author',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.UNSET,
        authorRole: void 0
      },
      RegistrationPublisher: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'registration.publisher@allangaunt.dev',
        name: 'Registration Publisher',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.UNSET,
        authorRole: void 0
      },
      RegistrationVaAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'registration.va.author@allangaunt.dev',
        name: 'Registration VA Author',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.UNSET,
        authorRole: void 0
      },
      RegistrationVaReviewer: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'registration.va.reviewer@allangaunt.dev',
        name: 'Registration VA Reviewer',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.UNSET,
        authorRole: void 0
      },
      NewAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'new.author@allangaunt.dev',
        name: 'New Author',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE
      },
      ReviewTranslationAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'review.translation.author@allangaunt.dev',
        name: 'Review Translation Author',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.PRO
      },
      ComplexOverdue: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'complex.overdue@allangaunt.dev',
        name: 'Reviewer for complex rules',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.TOP,
        authorRole: void 0
      },
      ReviewerNotClaimedBefore: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'reviewer.not.claimed.before@allangaunt.dev',
        name: 'Reviewer not claimed before',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        authorRole: void 0
      },
      ClaimNonMemberOutstandingReviewForThisTeam: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'claim.non.member.outstanding.review.for.this.team@allangaunt.dev',
        name: 'Claim non member outstanding review for this team',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        authorRole: void 0
      },
      AutoApproveOnTeam: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'auto.approve.on.team@allangaunt.dev',
        name: 'Auto approve on team',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: void 0
      },
      AutoBlockOnTeam: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'auto.block.on.team@allangaunt.dev',
        name: 'Auto block on team',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: void 0
      },
      NotificationTopReviewer: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'notification.top.reviewer@allangaunt.dev',
        name: 'Notification top reviewer',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.TOP,
        authorRole: void 0
      },
      TopReviewerApplicationRejected: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'tr.application.rejected@allangaunt.dev',
        name: 'Top Reviewer Application Rejected',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        rejectedTopReviewer: true
      },
      UserForRandomArcs: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'user.for.random.arcs@allangaunt.dev',
        name: 'User for random arcs',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.PUBLISHER
      },
      BlockNotTeamMember: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'block.not.team.member@allangaunt.dev',
        name: 'Block not team member',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: void 0
      },
      EbookAndAudioBook: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'ebookandaudiobook@allangaunt.dev',
        name: 'User hosting ebook and audio book at same time',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.PUBLISHER
      },
      EbookAndAudioBookReviewer: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'ebookandaudiobook.rev@allangaunt.dev',
        name: 'User reviewing ebook and audio book at same time',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: void 0
      },
      ArcForSameBook: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'arc.for.same.book@allangaunt.dev',
        name: 'Arc for same book user',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.BEST_SELLING
      },
      PaymentsGenericDecline: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.generic.decline@allangaunt.dev',
        name: 'Failed payments',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlV6WX0Oldj7O5'
      },
      Payments3DsCheck: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.3ds.check@allangaunt.dev',
        name: 'Payments 3Ds check',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlVGXvIcImjq5j'
      },
      PaymentsSuccess: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success@allangaunt.dev',
        name: 'Payments success',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlrRxjkzLd77pI'
      },
      PaymentsSuccessGrowingMonth: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success.growing.month@allangaunt.dev',
        name: 'Payments growing month',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlsYUJDEzKO2nl'
      },
      PaymentsSuccessGrowingYear: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success.growing.year@allangaunt.dev',
        name: 'Payments growing year',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlsaWmwh3vG3dl'
      },
      PaymentsSuccessProMonth: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success.pro.month@allangaunt.dev',
        name: 'Payments pro month',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlscPNH75eoYkc'
      },
      PaymentsSuccessProYear: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success.pro.year@allangaunt.dev',
        name: 'Payments pro year',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlsdSXHH5IbIeS'
      },
      PaymentsSuccessBestSellingMonth: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success.bestSelling.month@allangaunt.dev',
        name: 'Payments bestSelling month',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlsfozrTfMTk2l'
      },
      PaymentsSuccessBestSellingYear: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success.bestSelling.year@allangaunt.dev',
        name: 'Payments bestSelling year',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlsglEQAnp5rvc'
      },
      PaymentsSuccessPublisherMonth: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success.publisher.month@allangaunt.dev',
        name: 'Payments publisher month',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_RlspeFa6Ao9O8D'
      },
      PaymentsSuccessPublisherYear: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'payments.success.publisher.year@allangaunt.dev',
        name: 'Payments publisher year',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE,
        stripeCustomerId: 'cus_Rlspi5eIVilbO6'
      },
      ArcTeamRestrictions: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'arc.team.restrictions@allangaunt.dev',
        name: 'Arc team restrictions',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.GROWING
      },
      ArcTeamRestrictionsReader: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'arc.team.restrictions.reader@allangaunt.dev',
        name: 'Arc team restrictions (reader)',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: void 0
      },
      TooManyWithdrawals: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'too.many.withdrawals@allangaunt.dev',
        name: 'Too Many Withdrawals',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        authorRole: void 0
      },
      TooManyWithdrawalsAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'too.many.withdrawals.author@allangaunt.dev',
        name: 'Too Many Withdrawals - Author',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.PUBLISHER
      },
      PostmarkTransientBounce: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'Transient@bounce-testing.postmarkapp.com',
        name: 'Postmark transient bounce',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE
      },
      PostmarkSoftBounce: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'SoftBounce@bounce-testing.postmarkapp.com',
        name: 'Postmark soft bounce',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE
      },
      PostmarkDnsError: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'DnsError@bounce-testing.postmarkapp.com',
        name: 'Postmark dns error',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.FREE
      },
      // sc-26587: saved review copies email scenarios. SavedArcReader SAVES both ARCs (and
      // claims neither, so the emails fire); the fillers CLAIM the low-copies ARC to drop it
      // into the 1-5 range.
      SavedArcAuthor: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'savedarc.author@allangaunt.dev',
        name: 'Saved-Arc Test Author',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.PRO
      },
      SavedArcReader: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'savedarc.reader@allangaunt.dev',
        name: 'Saved-Arc Test Reader',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        authorRole: void 0
      },
      SavedArcFillers: [
        {
          id: this.getNextId(),
          ...this.base,
          emailAddress: 'savedarc.filler1@allangaunt.dev',
          name: 'Saved-Arc Filler One',
          reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
          authorRole: void 0
        },
        {
          id: this.getNextId(),
          ...this.base,
          emailAddress: 'savedarc.filler2@allangaunt.dev',
          name: 'Saved-Arc Filler Two',
          reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
          authorRole: void 0
        },
        {
          id: this.getNextId(),
          ...this.base,
          emailAddress: 'savedarc.filler3@allangaunt.dev',
          name: 'Saved-Arc Filler Three',
          reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
          authorRole: void 0
        }
      ],
      // sc-29038: a publisher nobody has lifted the live-campaign limit for, so it's held to the plan's
      // initial 10. Its campaigns are all scheduled and due (see CappedPublisherArcs): the status cron makes
      // 10 live, drafts the rest and sends one "cannot go live" notification.
      CappedPublisher: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'capped.publisher@allangaunt.dev',
        name: 'Capped Publisher',
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.NEW,
        authorRole: bsConstants.USER.ROLES.AUTHOR.PUBLISHER,
        maxRunningArcsOverride: null,
        // sc-29190: what it told us at checkout, waiting for staff on the Users page's "Publisher applications"
        publisherApplication: {
          website: 'https://www.cappedpublisher.example',
          information: 'A small press with 14 authors, mostly romance and cozy mystery. We release about 4 books a month and plan to run 15 to 20 campaigns a month.'
        }
      },
      // sc-29117: holds one claim per Due-or-Delayed case of a delayed or rejected Amazon review (the
      // DelayedAmazon* ARCs), on a reader of its own so those claims can't move the counts other specs assert.
      DelayedAmazonReader: {
        id: this.getNextId(),
        ...this.base,
        emailAddress: 'delayed.amazon@allangaunt.dev',
        name: 'Delayed Amazon Reader',
        authorRole: void 0,
        reviewerRole: bsConstants.USER.ROLES.REVIEWER.VALUED,
        lockRoles: true
      }
    }

    return {
      ...testData,
      RandomUsers: this.generateRandomData()
    }
  }
}
